import * as d3 from 'd3';
import { MarketAnalysisData, MarketMetadata, AggregatedVolumePoint } from '../../types';
import { ParsedChartPoint, ChartScales, ChartLayout } from '../types';
import { SCALE } from '../constants';
import { getMaxVolume } from '../../volumeUtils';

interface ScaleParams {
    parsedData: ParsedChartPoint[];
    analysisData: MarketAnalysisData | null;
    markets: MarketMetadata[];
    layout: ChartLayout;
    isFullSeriesMode: boolean;
    isLogScale: boolean;
    useDecimalScale: boolean;
    endDate?: Date;
    aggregatedVolume: AggregatedVolumePoint[];
    volumeMetric: 'volume' | 'tradeCount' | 'buySellVolume';
}

interface DomainResult {
    minValue: number;
    maxValue: number;
    allTimestamps: Date[];
}

function calculateDomain(
    parsedData: ParsedChartPoint[],
    analysisData: MarketAnalysisData | null,
    markets: MarketMetadata[],
    isFullSeriesMode: boolean,
    useDecimalScale: boolean
): DomainResult {
    let minValue = useDecimalScale ? 1 : 100;
    let maxValue = 0;
    const allTimestamps: Date[] = [];

    if (isFullSeriesMode && analysisData) {
        analysisData.bidAsk.forEach(d => {
            minValue = Math.min(minValue, d.bidPrice, d.askPrice);
            maxValue = Math.max(maxValue, d.bidPrice, d.askPrice);
            allTimestamps.push(d.timestamp);
        });
        analysisData.fairValue.forEach(d => {
            minValue = Math.min(minValue, d.lowerBound);
            maxValue = Math.max(maxValue, d.upperBound);
            allTimestamps.push(d.timestamp);
        });
        analysisData.trades.forEach(d => {
            minValue = Math.min(minValue, d.price);
            maxValue = Math.max(maxValue, d.price);
            allTimestamps.push(d.timestamp);
        });
        analysisData.candles?.forEach(d => {
            minValue = Math.min(minValue, d.low);
            maxValue = Math.max(maxValue, d.high);
            allTimestamps.push(d.timestamp);
        });
    } else {
        parsedData.forEach(point => {
            allTimestamps.push(point.parsedTimestamp);
            markets.forEach(market => {
                const value = point[`market_${market.id}`];
                if (value !== undefined && !isNaN(value)) {
                    minValue = Math.min(minValue, value);
                    maxValue = Math.max(maxValue, value);
                }
            });
        });
    }

    return { minValue, maxValue, allTimestamps };
}

export function createChartScales(params: ScaleParams): ChartScales & { timeDomain: [Date, Date] } {
    const {
        parsedData,
        analysisData,
        markets,
        layout,
        isFullSeriesMode,
        isLogScale,
        useDecimalScale,
        endDate,
        aggregatedVolume,
        volumeMetric,
    } = params;

    const { minValue, maxValue, allTimestamps } = calculateDomain(
        parsedData,
        analysisData,
        markets,
        isFullSeriesMode,
        useDecimalScale
    );

    const priceBuffer = (maxValue - minValue) * 0.05 || (useDecimalScale ? 0.05 : 5);

    // Time domain
    const timeExtent = d3.extent(allTimestamps) as [Date, Date];
    const timeDomain: [Date, Date] = endDate
        ? [timeExtent[0], new Date(Math.max(timeExtent[1].getTime(), endDate.getTime()))]
        : timeExtent;

    // X scale
    const x = d3.scaleTime()
        .domain(timeDomain)
        .range([0, layout.innerWidth]);

    // Y scale (price)
    const maxDomain = useDecimalScale ? SCALE.DECIMAL_MAX : SCALE.PERCENT_MAX;
    const minDomain = useDecimalScale ? SCALE.DECIMAL_MIN : SCALE.PERCENT_MIN;

    const y = isLogScale && minValue > 0
        ? d3.scaleLog()
            .domain([Math.max(minDomain, minValue - priceBuffer), maxValue + priceBuffer])
            .range([layout.priceHeight, 0])
            .nice()
        : d3.scaleLinear()
            .domain([Math.max(0, minValue - priceBuffer), Math.min(maxDomain, maxValue + priceBuffer)])
            .range([layout.priceHeight, 0])
            .nice();

    // Y scale (volume) - only if volume panel is shown
    let yVolume: d3.ScaleLinear<number, number> | undefined;
    if (layout.showVolume && analysisData?.candles && analysisData.candles.length > 0) {
        const maxVolumeValue = getMaxVolume(analysisData.candles, aggregatedVolume, volumeMetric) * 1.1;
        yVolume = d3.scaleLinear()
            .domain([0, maxVolumeValue])
            .range([layout.volumeHeight, 0]);
    }

    return { x, y, yVolume, timeDomain };
}

export function getLogTickValues(
    yDomain: [number, number],
    useDecimalScale: boolean
): number[] {
    const tickValues = useDecimalScale
        ? SCALE.LOG_TICK_VALUES_DECIMAL
        : SCALE.LOG_TICK_VALUES_PERCENT;
    return tickValues.filter(v => v >= yDomain[0] && v <= yDomain[1]);
}
